import { useState, useEffect, useMemo } from 'react';
import { AnalyticsConfig } from '../types/analytics.types';
import { AnalyticsConfigValidator, ValidationResult } from '../validation/analytics-config.validator';

export interface UseConfigValidationOptions {
  validateOnChange?: boolean;
  debounceMs?: number;
}

export interface UseConfigValidationReturn {
  validationResult: ValidationResult | null;
  isValidating: boolean;
  validate: () => void;
  hasErrors: boolean;
  hasWarnings: boolean;
  errorCount: number;
  warningCount: number;
  formatResults: () => string;
}

export const useConfigValidation = (
  config: AnalyticsConfig | null,
  options: UseConfigValidationOptions = {}
): UseConfigValidationReturn => {
  const { validateOnChange = true, debounceMs = 500 } = options;
  
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Debounced validation function
  const validate = useMemo(() => {
    let timeoutId: NodeJS.Timeout;

    return () => {
      if (!config) {
        setValidationResult(null);
        return;
      }

      setIsValidating(true);
      
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        try {
          const result = AnalyticsConfigValidator.validate(config);
          setValidationResult(result);
        } catch (error) {
          console.error('Validation error:', error);
          setValidationResult({
            isValid: false,
            errors: [{
              level: 'error',
              code: 'VALIDATION_EXCEPTION',
              message: error instanceof Error ? error.message : 'Unknown validation error',
              path: 'config'
            }],
            warnings: [],
            info: []
          });
        } finally {
          setIsValidating(false);
        }
      }, debounceMs);
    };
  }, [config, debounceMs]);

  // Auto-validate on config changes
  useEffect(() => {
    if (validateOnChange && config) {
      validate();
    }
  }, [config, validateOnChange, validate]);

  // Derived state
  const hasErrors = validationResult ? validationResult.errors.length > 0 : false;
  const hasWarnings = validationResult ? validationResult.warnings.length > 0 : false;
  const errorCount = validationResult ? validationResult.errors.length : 0;
  const warningCount = validationResult ? validationResult.warnings.length : 0;

  const formatResults = () => {
    if (!validationResult) return '';
    return AnalyticsConfigValidator.formatValidationResults(validationResult);
  };

  return {
    validationResult,
    isValidating,
    validate,
    hasErrors,
    hasWarnings,
    errorCount,
    warningCount,
    formatResults
  };
}; 